import { useEffect, useState } from 'react'
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getApiUrl } from '@/lib/subdomain'
import clsx from 'clsx'

const apiUrl = getApiUrl()

export function AdminLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [isAuthorized, setIsAuthorized] = useState(false)
  
  const { data: status, isLoading, error } = useQuery({
    queryKey: ['admin', 'setup-status'],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/admin/setup-status`, { 
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to check auth')
      return res.json()
    },
    retry: false,
    staleTime: 0,
  })
  
  useEffect(() => {
    if (isLoading) return
    
    if (error || !status) {
      navigate('/login')
      return
    }
    
    if (!status.isSetup) {
      navigate('/login?setup=true')
      return
    }
    
    if (!status.isLoggedIn) {
      navigate('/login')
      return
    }
    
    setIsAuthorized(true)
  }, [status, isLoading, error, navigate])
  
  const handleLogout = async () => {
    await fetch(`${apiUrl}/admin/logout`, { 
      method: 'POST',
      credentials: 'include',
    })
    setIsAuthorized(false)
    navigate('/login')
  }
  
  if (isLoading || !isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    )
  }
  
  const navItems = [
    { path: '/', label: 'Dashboard', icon: '📊' },
    { path: '/accounts', label: 'Gaming Accounts', icon: '🎮' },
    { path: '/credentials', label: 'API Keys', icon: '🔑' },
    { path: '/settings', label: 'Settings', icon: '⚙️' },
  ]
  
  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900/50 border-r border-gray-800 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-gray-800">
          <a href="https://n0n3.com" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-purple-500 flex items-center justify-center font-bold text-lg">
              n0
            </div>
            <div>
              <span className="text-xl font-display font-bold">n0n3</span>
              <span className="text-xs text-gray-500 uppercase tracking-wider block">Admin Panel</span>
            </div>
          </a>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink 
              key={item.path}
              to={item.path} 
              icon={item.icon}
              current={location.pathname === item.path || (item.path === '/' && location.pathname === '')}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-800 space-y-2">
          <a 
            href="https://n0n3.com" 
            target="_blank" 
            rel="noopener"
            className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors text-sm"
          >
            <span>🌐</span>
            <span>View Site</span>
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors text-sm"
          >
            <span>🚪</span>
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
      
      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

function NavLink({ 
  to, 
  children, 
  icon, 
  current 
}: { 
  to: string
  children: React.ReactNode
  icon: string
  current: boolean 
}) {
  return (
    <Link
      to={to}
      className={clsx(
        'flex items-center gap-3 px-4 py-3 rounded-xl transition-all',
        current 
          ? 'bg-gradient-to-r from-primary-500/20 to-purple-500/10 text-white border border-primary-500/30' 
          : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
      )}
    >
      <span className="text-lg">{icon}</span>
      <span className="font-medium">{children}</span>
    </Link>
  )
}
